import history from '-/utils/history'
import { setAuthority } from '-/utils/authority'
import { reloadAuthorized } from '-/utils/Authorized'

import axios, {
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  CancelTokenSource,
  AxiosResponse,
} from 'axios'
import { BusinessError, CancelError, NetError } from './errors'

function mockAxios<T extends ApiData>(ms: number, data: T): AxiosPromise<T> {
  return new Promise(resolve =>
    setTimeout(
      () =>
        resolve({
          data,
          status: 200,
          statusText: 'ok',
          headers: {},
          config: {},
        }),
      ms
    )
  )
}

export interface MockConfig {
  delayTime: number
  mockData: Partial<ApiData>
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ApiData<T = any> {
  errcode?: number
  code?: number
  data: T
  errmsg: string
}

type CustomError = BusinessError | CancelError | NetError

type Executor<S> = (
  resolve: (data: S, res: AxiosResponse<S>) => void,
  reject: (error: CustomError) => void
) => void

export class AxiosCancelablePromise<T> extends Promise<T> {
  public cancelTokenSource: CancelTokenSource

  public constructor(executor: Executor<T>, cancelTokenSource: CancelTokenSource) {
    super(executor)

    this.cancelTokenSource = cancelTokenSource
  }

  public cancel(): void {
    this.cancelTokenSource.cancel()
  }
}

class Wrapper {
  public axiosInstance: AxiosInstance

  public constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance
  }

  public static wrapRequest(
    request: AxiosPromise<ApiData>,
    cancelTokenSource: CancelTokenSource
  ): AxiosCancelablePromise<ApiData> {
    return new AxiosCancelablePromise<ApiData>((resolve, reject) => {
      request
        .then(res => {
          const { data } = res
          if (data.errcode === -401 || data.errcode === 401) {
            setAuthority('')
            reloadAuthorized()
            history.replace('/user')
          }

          if (data.errcode !== 0) {
            return reject(new BusinessError(Number(data.errcode), data.errmsg))
          }

          return resolve(data, res)
        })
        .catch(error => {
          if (axios.isCancel(error)) {
            return reject(new CancelError('Request canceled'))
          }

          const { response } = error
          if (response) {
            const responseData = response.data || {}
            return reject(new NetError(response.status, responseData.errmsg))
          }

          return reject(error)
        })
    }, cancelTokenSource)
  }

  public head(url: string, config?: AxiosRequestConfig): AxiosCancelablePromise<ApiData> {
    const cancelTokenSource = axios.CancelToken.source()

    return Wrapper.wrapRequest(
      this.axiosInstance.head(
        url,
        Object.assign({}, config, {
          withCredentials: true,
          cancelToken: cancelTokenSource.token,
        })
      ),
      cancelTokenSource
    )
  }

  public get(url: string, config?: AxiosRequestConfig): AxiosCancelablePromise<ApiData> {
    const cancelTokenSource = axios.CancelToken.source()

    return Wrapper.wrapRequest(
      this.axiosInstance.get(
        url,
        Object.assign({}, config, {
          withCredentials: true,
          cancelToken: cancelTokenSource.token,
        })
      ),
      cancelTokenSource
    )
  }

  public delete(
    url: string,
    data?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    config?: AxiosRequestConfig
  ): AxiosCancelablePromise<ApiData> {
    const cancelTokenSource = axios.CancelToken.source()

    return Wrapper.wrapRequest(
      this.axiosInstance.delete(
        url,
        Object.assign({}, config, {
          data,
          withCredentials: true,
          cancelToken: cancelTokenSource.token,
        })
      ),
      cancelTokenSource
    )
  }

  public post(
    url: string,
    data?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    config?: AxiosRequestConfig
  ): AxiosCancelablePromise<ApiData> {
    const cancelTokenSource = axios.CancelToken.source()

    return Wrapper.wrapRequest(
      this.axiosInstance.post(
        url,
        data,
        Object.assign({}, config, {
          withCredentials: true,
          cancelToken: cancelTokenSource.token,
        })
      ),
      cancelTokenSource
    )
  }

  public put(
    url: string,
    data?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    config?: AxiosRequestConfig
  ): AxiosCancelablePromise<ApiData> {
    const cancelTokenSource = axios.CancelToken.source()

    return Wrapper.wrapRequest(
      this.axiosInstance.put(
        url,
        data,
        Object.assign({}, config, {
          withCredentials: true,
          cancelToken: cancelTokenSource.token,
        })
      ),
      cancelTokenSource
    )
  }

  public patch(
    url: string,
    data?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    config?: AxiosRequestConfig
  ): AxiosCancelablePromise<ApiData> {
    const cancelTokenSource = axios.CancelToken.source()

    return Wrapper.wrapRequest(
      this.axiosInstance.patch(
        url,
        data,
        Object.assign({}, config, {
          withCredentials: true,
          cancelToken: cancelTokenSource.token,
        })
      ),
      cancelTokenSource
    )
  }

  // eslint-disable-next-line class-methods-use-this
  public mockGet(
    url: string,
    config?: AxiosRequestConfig,
    mockConfig: MockConfig = {
      delayTime: 1000,
      mockData: { errcode: 0, data: undefined, errmsg: '' },
    }
  ): AxiosCancelablePromise<ApiData> {
    const cancelTokenSource = axios.CancelToken.source()

    const {
      delayTime = 1000,
      mockData: { errcode = 0, errmsg = '', data = null },
    } = mockConfig

    return Wrapper.wrapRequest(mockAxios(delayTime, { errcode, data, errmsg }), cancelTokenSource)
  }

  // eslint-disable-next-line class-methods-use-this
  public mockDelete(
    url: string,
    config?: AxiosRequestConfig,
    mockConfig: MockConfig = {
      delayTime: 1000,
      mockData: { errcode: 0, data: true, errmsg: '' },
    }
  ): AxiosCancelablePromise<ApiData> {
    const cancelTokenSource = axios.CancelToken.source()

    const {
      delayTime = 2000,
      mockData: { errcode = 0, errmsg = '', data = true },
    } = mockConfig

    return Wrapper.wrapRequest(mockAxios(delayTime, { errcode, data, errmsg }), cancelTokenSource)
  }
}

export default Wrapper
